// frontend-react/src/api/api.js (ΠΛΗΡΕΣ ΚΑΙ ΕΝΗΜΕΡΩΜΕΝΟ)

import axios from 'axios';

const LOCAL_EXECUTION = process.env.REACT_APP_LOCAL_EXECUTION === 'true';
const hashSession = new URLSearchParams(window.location.hash.slice(1)).get('local-session');
const storedLocalSession = sessionStorage.getItem('localSession') || localStorage.getItem('algostockLocalSession');
const localSession = hashSession || process.env.REACT_APP_LOCAL_API_TOKEN || storedLocalSession || null;
if (LOCAL_EXECUTION && localSession) {
    sessionStorage.setItem('localSession', localSession);
    localStorage.setItem('algostockLocalSession', localSession);
    if (hashSession) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
}
const EXPLICIT_API_URL = process.env.REACT_APP_API_URL;
const API_URL = EXPLICIT_API_URL
    ? EXPLICIT_API_URL
    : (LOCAL_EXECUTION ? window.location.origin : '/api');

const api = axios.create({
    baseURL: API_URL,
});

api.interceptors.request.use(
    config => {
        if (LOCAL_EXECUTION) config.headers['X-AlgoStock-Local-Token'] = sessionStorage.getItem('localSession') || '';
        const token = localStorage.getItem('authToken');
        // Μην επισυνάπτεις το Authorization header αν υπάρχει το temp-token
        if (token && !config.headers['temp-token']) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

export default api;

// --- Authentication ---
export const loginUser = (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    return api.post('/auth/token', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
};
export const registerUser = (username, email, password, termsAccepted) => 
    api.post('/auth/register', { username, email, password, termsAccepted });
export const getCurrentUser = () => api.get('/users/me');
export const markOnboardingComplete = () => api.post('/users/me/onboarding-complete');
export const forgotPassword = (data) => api.post('/auth/forgot-password', data);
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });

// --- 2FA Authentication ---
export const verify2FACode = (tempToken, code) => api.post('/auth/token/2fa', { code }, { headers: { 'temp-token': tempToken } });
export const verify2FARecoveryCode = (tempToken, code) => api.post('/auth/token/2fa-recovery', { temp_token: tempToken, code });
export const generate2FASecret = () => api.post('/users/me/2fa/generate');
const rotateSecuritySession = async (request) => {
    const response = await request;
    if (response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
    }
    return response;
};
export const enable2FA = (data) => rotateSecuritySession(api.post('/users/me/2fa/enable', data));
export const disable2FA = (data) => rotateSecuritySession(api.post('/users/me/2fa/disable', data));

// --- Bot Control & Status ---
export const getBotStatus = () => LOCAL_EXECUTION ? api.get('/bot/status') : Promise.resolve({ data: { is_running: false, available: false } });
export const toggleBotStatus = (isRunning) => isRunning ? api.post('/bot/stop') : api.post('/bot/start');
export const emergencyStop = () => api.post('/bot/emergency-stop');
export const getLocalHealth = () => LOCAL_EXECUTION ? api.get('/local/health') : Promise.resolve({ data: { mode: 'cloud', available: false } });
export const getMarketStatus = () => api.get('/data/market_status');
export const liquidateAll = () => api.post('/actions/liquidate');
export const liquidatePosition = (ticker) => api.post('/actions/liquidate/' + encodeURIComponent(ticker), {confirm:true});

// --- Watchlist & Strategies ---
export const getWatchlist = () => api.get('/watchlist/');
export const addWatchlistItem = (payload) => api.post('/watchlist/', payload);
export const deleteWatchlistItem = (itemId) => api.delete(`/watchlist/${itemId}`);
export const updateWatchlistItem = (itemId, data) => api.put(`/watchlist/${itemId}`, data);

// --- Settings ---
export const getGlobalSettings = () => api.get('/settings/global');
export const updateGlobalSettings = (settings) => api.put('/settings/global', settings);
export const getRiskProfiles = () => api.get('/settings/risk_profiles');
export const updateRiskProfiles = (profiles) => api.put('/settings/risk_profiles', profiles);

// --- Broker Connections ---
export const getBrokerConnections = () => api.get('/broker/');
export const addBrokerConnection = (data) => api.post('/broker/', data);
export const deleteBrokerConnection = (connId) => api.delete(`/broker/${connId}`);
export const setActiveBroker = (connId) => api.post(`/broker/${connId}/set-active`);
export const testBrokerConnection = (connId) => api.post(`/broker/${connId}/test`);

// --- Data & Analytics ---
export const getLivePositions = () => api.get('/data/live_positions');
export const getOpenOrders = () => api.get('/data/open_orders');
export const getTradeHistory = () => api.get('/data/trade_history');
export const getEquityCurve = () => api.get('/data/equity_curve');
export const getPerformanceStats = () => api.get('/data/performance_stats');
export const getLogs = () => api.get('/data/logs');

// --- Actions ---
export const modifyStopLoss = (ticker, new_stop_price) => api.post('/actions/modify_stop', { ticker, new_stop_price });

// --- Admin ---
export const adminGetAllUsers = () => api.get('/admin/users');
export const adminCreateUser = (data) => api.post('/admin/users', data);
export const adminUpdateUser = (userId, data) => api.put(`/admin/users/${userId}`, data);
export const adminDeleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const adminGetUserTrades = (userId) => api.get(`/admin/users/${userId}/trade_history`);
export const adminGetUserPositions = (userId) => api.get(`/admin/users/${userId}/positions`);
export const adminGetUserWatchlist = (userId) => api.get(`/admin/users/${userId}/watchlist`);

// --- Notifications ---
export const getNotifications = () => api.get('/notifications/');
export const markNotificationsAsRead = () => api.post('/notifications/mark-as-read');

// --- Market Intelligence ---
export const getIntelligenceStatus = () => api.get(LOCAL_EXECUTION ? '/intelligence/status' : '/commercial/intelligence/status');
export const getIntelligenceLatest = () => api.get(LOCAL_EXECUTION ? '/intelligence/latest' : '/commercial/intelligence/snapshot');
export const getDemoAnalytics = () => api.get('/intelligence/demo-analytics');
export const runIntelligenceScanner = () => api.post('/intelligence/scanner/run');
export const runOpeningScanner = () => api.post('/intelligence/opening/run');
export const runFullIntelligencePipeline = () => api.post('/intelligence/pipeline/run');
export const getMarketDataProviderStatus = () => api.get('/intelligence/provider/status');
export const configureFmpProvider = (api_key) => api.post('/intelligence/provider/configure', { api_key });
export const testFmpProvider = () => api.post('/intelligence/provider/test');
export const removeFmpProvider = () => api.delete('/intelligence/provider/configure');
export const validateFmpProvider = () => api.get('/intelligence/provider/validate');

// --- Stripe / Billing ---
export const getBillingStatus = () => api.get('/stripe/status');
export const getMethodology = () => api.get('/commercial/methodology');
export const createCheckoutSession = () => api.post('/stripe/create-checkout-session');
export const createPortalSession = () => api.post('/stripe/create-portal-session');

// --- Local Product / Commercialization ---
export const getLocalProductStatus = () => api.get('/local/product/status');
export const getLocalSystemStatus = () => api.get('/local/product/system');
export const getResearchValidationStatus = () => api.get('/local/product/research-validation');
export const detectIbkrPaper = () => api.get('/local/product/ibkr/detect');
export const autoConfigureIbkrPaper = () => api.post('/local/product/ibkr/autoconfigure');
export const createLocalBackup = () => api.post('/local/product/backup');


// --- Portfolio Risk Ledger ---
export const getRiskCapacity = () => api.get('/data/risk');

// --- Immutable Trade Plans ---
export const previewPlanRisk = (payload) => api.post('/plans/risk-preview', payload);
export const createTradePlan = (payload) => api.post('/plans', payload);
export const getTradePlans = (symbol = null) => api.get('/plans', { params: symbol ? { symbol } : {} });
export const getTradePlan = (planId) => api.get(`/plans/${planId}`);
export const addTradePlanEvent = (planId, event_type, payload = {}) =>
    api.post(`/plans/${planId}/events`, { event_type, payload });
export const exportTradePlan = (planId) => api.post(`/plans/${planId}/export`);
export const recordExternalTradeClose = (planId, payload) => api.post(`/plans/${planId}/external-close`, payload);
export const executeTradePlanPaper = (planId, confirmEventContext = true) =>
    api.post(`/plans/${planId}/execute-paper`, {
        confirm_frozen_plan: true,
        confirm_event_context: Boolean(confirmEventContext),
    });
